import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { ParamSettings, MercantileType } from "../../models/ParamSettings";
import { RoleType } from "../../models/Role";
import { activeUser } from "../../services/AuthService";
import {
  createSettings,
  updateOneSettings,
} from "../../services/SettingsService";
import "./NewSettingsForm.scss";

export interface NewSettingsFormProps {
  handleNewParamSettings: (newSettings: ParamSettings) => void;
  settings?: ParamSettings;
  buttonLabel?: string;
  buttonIcon?: string;
  buttonStyle?: React.CSSProperties;
  buttonSaveStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
}

export const NewSettingsForm = (p: NewSettingsFormProps) => {
  const {
    settings,
    handleNewParamSettings,
    buttonLabel,
    buttonIcon,
    buttonStyle,
    buttonSaveStyle,
    buttonResetStyle,
  } = p;

  const { t } = useTranslation();
  const toast = useRef<any>(null);

  const [visibleNewSettingsForm, setVisibleNewSettingsForm] =
    useState<boolean>(false);
  const [newSettings, setNewSettings] = useState<ParamSettings>({
    id: settings?.id ?? "",
    mercantile: settings?.mercantile ?? ({} as MercantileType),
    moisture: settings?.moisture as number,
    moistureMax: settings?.moistureMax as number,
    moistureMin: settings?.moistureMin as number,
    impurity: settings?.impurity as number,
    impurityMax: settings?.impurityMax as number,
    impurityMin: settings?.impurityMin as number,
    breakage: settings?.breakage ?? undefined,
    breakageMax: settings?.breakageMax ?? undefined,
    breakageMin: settings?.breakageMin ?? undefined,
    defect: settings?.defect ?? undefined,
    defectMax: settings?.defectMax ?? undefined,
    defectMin: settings?.defectMin ?? undefined,
    hectoliter: settings?.hectoliter ?? undefined,
    hectoliterMax: settings?.hectoliterMax ?? undefined,
    hectoliterMin: settings?.hectoliterMin ?? undefined,
    calculateCalo: settings?.calculateCalo ?? true,
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  useEffect(() => {
    if (settings) {
      setNewSettings(settings);
      reset();
    }
  }, [settings]);

  useEffect(() => {}, [newSettings]);

  const showModal = () => {
    setVisibleNewSettingsForm(!visibleNewSettingsForm ? true : false);
  };

  const handleNewSettingsForm = (prop: string, value: any) => {
    setNewSettings((newSettings) => ({
      ...newSettings,
      [prop]: value,
    }));
  };

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const onSubmit = async () => {
    try {
      if (newSettings.id) {
        const resp = await updateOneSettings(newSettings.id, newSettings);
        ensureSuccess(resp);
        handleNewParamSettings(resp?.data?.result ?? newSettings);
      } else {
        const resp = await createSettings(newSettings);
        ensureSuccess(resp);
        handleNewParamSettings(resp?.data?.result ?? newSettings);
      }

      handleResetModal();

      toast.current?.show({
        severity: "success",
        detail: t(`Uspešno dodati novi podaci`),
        life: 3000,
      });
    } catch (e: any) {
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          e?.message ??
          String(t("Greška pri snimanju")),
        life: 3500,
      });
    }
  };

  const onError = () => {
    toast.current.show({
      severity: "error",
      detail: t(`Neispravno popunjena polja`),
      life: 3000,
    });
  };

  const handleResetModal = () => {
    showModal();
  };

  let addButtonStyle: React.CSSProperties = {};
  if (buttonStyle) {
    addButtonStyle = buttonStyle;
  } else {
    addButtonStyle.background = "rgba(0, 165, 83, 1)";
    addButtonStyle.border = "none";
  }

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(108, 117, 125, 1)";
    addButtonResetStyle.border = "none";
  }

  const header = (
    <div className="header">
      <span className="p-dialog-titlebar-icon p-link">
        <i className="bi bi-pencil" style={{ fontSize: 18 }}></i>
      </span>
      <span>
        {t(newSettings.mercantile)} - {t(`ažuriranje parametara`)}
      </span>
    </div>
  );

  const footer = (
    <div className="footer-buttons">
      <ButtonInput
        labelText={t("Sačuvaj")}
        onButtonClick={handleSubmit(onSubmit, onError)}
        addStyle={addButtonSaveStyle}
        disabled={
          !(
            activeUser.role?.name === RoleType.Superadmin ||
            activeUser.role?.name === RoleType.Administrator
          )
        }
      />
      <ButtonInput
        labelText={t("Otkaži")}
        onButtonClick={handleResetModal}
        addStyle={addButtonResetStyle}
        disabled={
          !(
            activeUser.role?.name === RoleType.Superadmin ||
            activeUser.role?.name === RoleType.Administrator
          )
        }
      />
    </div>
  );

  return (
    <>
      <ButtonInput
        labelText={buttonLabel as string}
        icon={buttonIcon}
        onButtonClick={showModal}
        addStyle={addButtonStyle}
      />
      <Toast ref={toast} position={"top-right"} />
      {visibleNewSettingsForm ? (
        <div>
          <Dialog
            header={header}
            visible={true}
            style={{ width: "50vw" }}
            className="new-settings-modal"
            footer={footer}
            blockScroll={false}
            draggable={false}
            onHide={showModal}
          >
            <div className="new-settings-form-body">
              <TextInputWithIcon
                id="moisture"
                value={newSettings.moisture as number}
                onValueChange={handleNewSettingsForm}
                labelText={`${t("Vlaga SRPS")} *`}
                icon="bi bi-pencil"
                iconAlign="left"
                placeholderText={t("Unesite SRPS procenat vlage")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  required: true,
                  pattern: /^\d+(\.\d{2})?$/,
                  min: 0,
                  max: 100,
                }}
                disabled={
                  !(
                    activeUser.role?.name === RoleType.Superadmin ||
                    activeUser.role?.name === RoleType.Administrator
                  )
                }
              />
              <TextInputWithIcon
                id="moistureMin"
                value={newSettings.moistureMin as number}
                onValueChange={handleNewSettingsForm}
                labelText={`${t("Minimalna vlaga")} *`}
                icon="bi bi-pencil"
                iconAlign="left"
                placeholderText={t("Unesite minimalni procenat vlage")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  required: true,
                  pattern: /^\d+(\.\d{2})?$/,
                  min: 0,
                  max: 100,
                }}
                disabled={
                  !(
                    activeUser.role?.name === RoleType.Superadmin ||
                    activeUser.role?.name === RoleType.Administrator
                  )
                }
              />
              <TextInputWithIcon
                id="moistureMax"
                value={newSettings.moistureMax as number}
                onValueChange={handleNewSettingsForm}
                labelText={`${t("Maksimalna vlaga")} *`}
                icon="bi bi-pencil"
                iconAlign="left"
                placeholderText={t("Unesite maksimalni procenat vlage")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  required: true,
                  pattern: /^\d+(\.\d{2})?$/,
                  min: 0,
                  max: 100,
                }}
                disabled={
                  !(
                    activeUser.role?.name === RoleType.Superadmin ||
                    activeUser.role?.name === RoleType.Administrator
                  )
                }
              />
              <TextInputWithIcon
                id="impurity"
                value={newSettings.impurity as number}
                onValueChange={handleNewSettingsForm}
                labelText={`${t("Primese SRPS")} *`}
                icon="bi bi-pencil"
                iconAlign="left"
                placeholderText={t("Unesite SRPS primese")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  required: true,
                  pattern: /^\d+(\.\d{2})?$/,
                  min: 0,
                  max: 100,
                }}
                disabled={
                  !(
                    activeUser.role?.name === RoleType.Superadmin ||
                    activeUser.role?.name === RoleType.Administrator
                  )
                }
              />
              <TextInputWithIcon
                id="impurityMin"
                value={newSettings.impurityMin as number}
                onValueChange={handleNewSettingsForm}
                labelText={`${t("Minimalne primese")} *`}
                icon="bi bi-pencil"
                iconAlign="left"
                placeholderText={t("Unesite minimalne primese")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  required: true,
                  pattern: /^\d+(\.\d{2})?$/,
                  min: 0,
                  max: 100,
                }}
                disabled={
                  !(
                    activeUser.role?.name === RoleType.Superadmin ||
                    activeUser.role?.name === RoleType.Administrator
                  )
                }
              />
              <TextInputWithIcon
                id="impurityMax"
                value={newSettings.impurityMax as number}
                onValueChange={handleNewSettingsForm}
                labelText={`${t("Maksimalne primese")} *`}
                icon="bi bi-pencil"
                iconAlign="left"
                placeholderText={t("Unesite maksimalne primese")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  required: true,
                  pattern: /^\d+(\.\d{2})?$/,
                  min: 0,
                  max: 100,
                }}
                disabled={
                  !(
                    activeUser.role?.name === RoleType.Superadmin ||
                    activeUser.role?.name === RoleType.Administrator
                  )
                }
              />
              {newSettings.mercantile === MercantileType.Corn && (
                <TextInputWithIcon
                  id="breakage"
                  value={newSettings.breakage as number}
                  onValueChange={handleNewSettingsForm}
                  labelText={`${t("Lom SRPS")} *`}
                  icon="bi bi-pencil"
                  iconAlign="left"
                  placeholderText={t("Unesite SRPS lom")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    required: true,
                    pattern: /^\d+(\.\d{2})?$/,
                    min: 0,
                    max: 100,
                  }}
                  disabled={
                    !(
                      activeUser.role?.name === RoleType.Superadmin ||
                      activeUser.role?.name === RoleType.Administrator
                    )
                  }
                />
              )}
              {newSettings.mercantile === MercantileType.Corn && (
                <TextInputWithIcon
                  id="breakageMin"
                  value={newSettings.breakageMin as number}
                  onValueChange={handleNewSettingsForm}
                  labelText={`${t("Minimalni lom")} *`}
                  icon="bi bi-pencil"
                  iconAlign="left"
                  placeholderText={t("Unesite minimalni lom")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    required: true,
                    pattern: /^\d+(\.\d{2})?$/,
                    min: 0,
                    max: 100,
                  }}
                  disabled={
                    !(
                      activeUser.role?.name === RoleType.Superadmin ||
                      activeUser.role?.name === RoleType.Administrator
                    )
                  }
                />
              )}
              {newSettings.mercantile === MercantileType.Corn && (
                <TextInputWithIcon
                  id="breakageMax"
                  value={newSettings.breakageMax as number}
                  onValueChange={handleNewSettingsForm}
                  labelText={`${t("Maksimalni lom")} *`}
                  icon="bi bi-pencil"
                  iconAlign="left"
                  placeholderText={t("Unesite maksimalni lom")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    required: true,
                    pattern: /^\d+(\.\d{2})?$/,
                    min: 0,
                    max: 100,
                  }}
                  disabled={
                    !(
                      activeUser.role?.name === RoleType.Superadmin ||
                      activeUser.role?.name === RoleType.Administrator
                    )
                  }
                />
              )}
              {newSettings.mercantile === MercantileType.Corn && (
                <TextInputWithIcon
                  id="defect"
                  value={newSettings.defect as number}
                  onValueChange={handleNewSettingsForm}
                  labelText={`${t("Defekt SRPS")} *`}
                  icon="bi bi-pencil"
                  iconAlign="left"
                  placeholderText={t("Unesite SRPS defekt")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    required: true,
                    pattern: /^\d+(\.\d{2})?$/,
                    min: 0,
                    max: 100,
                  }}
                  disabled={
                    !(
                      activeUser.role?.name === RoleType.Superadmin ||
                      activeUser.role?.name === RoleType.Administrator
                    )
                  }
                />
              )}
              {newSettings.mercantile === MercantileType.Corn && (
                <TextInputWithIcon
                  id="defectMin"
                  value={newSettings.defectMin as number}
                  onValueChange={handleNewSettingsForm}
                  labelText={`${t("Minimalni defekt")} *`}
                  icon="bi bi-pencil"
                  iconAlign="left"
                  placeholderText={t("Unesite minimalni defekt")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    required: true,
                    pattern: /^\d+(\.\d{2})?$/,
                    min: 0,
                    max: 100,
                  }}
                  disabled={
                    !(
                      activeUser.role?.name === RoleType.Superadmin ||
                      activeUser.role?.name === RoleType.Administrator
                    )
                  }
                />
              )}
              {newSettings.mercantile === MercantileType.Corn && (
                <TextInputWithIcon
                  id="defectMax"
                  value={newSettings.defectMax as number}
                  onValueChange={handleNewSettingsForm}
                  labelText={`${t("Maksimalni defekt")} *`}
                  icon="bi bi-pencil"
                  iconAlign="left"
                  placeholderText={t("Unesite maksimalni defekt")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    required: true,
                    pattern: /^\d+(\.\d{2})?$/,
                    min: 0,
                    max: 100,
                  }}
                  disabled={
                    !(
                      activeUser.role?.name === RoleType.Superadmin ||
                      activeUser.role?.name === RoleType.Administrator
                    )
                  }
                />
              )}
              {(newSettings.mercantile === MercantileType.Wheat ||
                newSettings.mercantile === MercantileType.Barley) && (
                <TextInputWithIcon
                  id="hectoliter"
                  value={newSettings.hectoliter as number}
                  onValueChange={handleNewSettingsForm}
                  labelText={`${t("Hektolitar")} *`}
                  icon="bi bi-pencil"
                  iconAlign="left"
                  placeholderText={t("Unesite hektolitar")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    required: true,
                    pattern: /^\d+(\.\d{2})?$/,
                    min: 0,
                    max: 100,
                  }}
                  disabled={
                    !(
                      activeUser.role?.name === RoleType.Superadmin ||
                      activeUser.role?.name === RoleType.Administrator
                    )
                  }
                />
              )}
              {(newSettings.mercantile === MercantileType.Wheat ||
                newSettings.mercantile === MercantileType.Barley) && (
                <TextInputWithIcon
                  id="hectoliterMin"
                  value={newSettings.hectoliterMin as number}
                  onValueChange={handleNewSettingsForm}
                  labelText={`${t("Minimalni hektolitar")} *`}
                  icon="bi bi-pencil"
                  iconAlign="left"
                  placeholderText={t("Unesite minimalni hektolitar")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    required: true,
                    pattern: /^\d+(\.\d{2})?$/,
                    min: 0,
                    max: 100,
                  }}
                  disabled={
                    !(
                      activeUser.role?.name === RoleType.Superadmin ||
                      activeUser.role?.name === RoleType.Administrator
                    )
                  }
                />
              )}
              {(newSettings.mercantile === MercantileType.Wheat ||
                newSettings.mercantile === MercantileType.Barley) && (
                <TextInputWithIcon
                  id="hectoliterMax"
                  value={newSettings.hectoliterMax as number}
                  onValueChange={handleNewSettingsForm}
                  labelText={`${t("Maksimalni hektolitar")} *`}
                  icon="bi bi-pencil"
                  iconAlign="left"
                  placeholderText={t("Unesite maksimalni hektolitar")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{
                    required: true,
                    pattern: /^\d+(\.\d{2})?$/,
                    min: 0,
                    max: 100,
                  }}
                  disabled={
                    !(
                      activeUser.role?.name === RoleType.Superadmin ||
                      activeUser.role?.name === RoleType.Administrator
                    )
                  }
                />
              )}
              <RadioButtonInput
                id="calculateCalo"
                value={newSettings.calculateCalo as boolean}
                radioButtonValue1={t("DA")}
                radioButtonValue2={t("NE")}
                onValueChange={handleNewSettingsForm}
                labelText={t("Obračunavati kalo/rastur")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={
                  !(
                    activeUser.role?.name === RoleType.Superadmin ||
                    activeUser.role?.name === RoleType.Administrator
                  )
                }
              />
            </div>
          </Dialog>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
